import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {NG_VALUE_ACCESSOR} from '@angular/forms';

@Component({
  selector: 'app-pin-input',
  templateUrl: './pin-input.component.html',
  styleUrls: ['./pin-input.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PinInputComponent), multi: true},
  ],
})
export class PinInputComponent implements AfterViewInit {
  @Input() isLarge: boolean;
  @Input() disabled: boolean;
  @Input() inputsType = 'password';
  @Input() alibiError: boolean;
  @Input() validateIncorrectPIN = true;
  @Input() validateSequence: boolean;
  @Input() errorPinCountMessage = 'Your PIN is blocked.';

  @Input() set focused(value: boolean) {
    this.focusedInner = value;

    if (value && this.pinInputList) {
      setTimeout(() => this.pinInputList.first.nativeElement.focus());
    }
  }

  @Input() set invalid(value: boolean) {
    this.invalidInner = value;

    if (value && this.validateIncorrectPIN) {
      this.countRemaining--;

      if (this.countRemaining === 0) {
        this.disabled = true;
        this.clearInputs();
      }
    }
  }

  get invalid(): boolean {
    return this.invalidInner;
  }

  @Output() pinValueComplete: EventEmitter<string> = new EventEmitter();

  @ViewChildren('pinInput') pinInputList: QueryList<ElementRef>;

  focusInner = false;
  invalidInner: boolean;
  pinInputs: string[] = Array(4).fill(null);

  countRemaining = 3;
  focusedInner: boolean;

  sequenceInvalid: boolean;
  sequenceMessage: string;

  incorrectPIN: boolean;

  onChangeCallback: (v: any) => void;
  onTouchCallback: (v: any) => void;

  ngAfterViewInit(): void {
    if (this.pinInputList && this.focusedInner) {
      setTimeout(() => this.pinInputList.first.nativeElement.focus());
    }
  }

  writeValue(v: any): void {
    if (!v) {
      this.clearInputs();
    }
  }

  registerOnChange(fn: (v: any) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: (v: any) => void): void {
    this.onTouchCallback = fn;
  }

  valueInput(ev: KeyboardEvent, index: number): void {
    const allow: boolean = !!ev.code.match(/Arrow|Delete|Backspace|Tab|Digit|Numpad/);
    const isNumberKey: boolean = !!ev.key.match(/[0-9]/);

    if (allow) {
      const nextIndex = index + 1 < this.pinInputs.length ? index + 1 : 0;
      const prevIndex = index - 1 >= 0 ? index - 1 : this.pinInputs.length - 1;

      if (isNumberKey) {
        this.invalidInner = false;
        this.sequenceInvalid = false;
        this.pinInputs[index] = ev.key;

        setTimeout(() => {
          if (this.pinInputs.join('').length !== 4) {
            this.pinInputList.get(nextIndex).nativeElement.focus();
          }
        });

        if (this.pinInputs.every(el => !!el)) {
          if (this.validateSequence) {
            this.sequenceMessage = '';

            const sequentialAsc = this.pinInputs.every((item, i) => {
              return i === +this.pinInputs.length - 1 || +item + 1 === +this.pinInputs[i + 1];
            });

            const sequentialDesc = this.pinInputs.every((item, i) => {
              return i === 0 || +item + 1 === +this.pinInputs[i - 1];
            });

            const duplicatedChainAsc = this.pinInputs.some((item, i) => {
              return i < +this.pinInputs.length - 2 &&
                (+this.pinInputs[i] === +this.pinInputs[i + 1] &&
                  +this.pinInputs[i] === +this.pinInputs[i + 2]);
            });

            const duplicatedChainDesc = this.pinInputs.some((item, i) => {
              return i > 1 &&
                (+this.pinInputs[i] === +this.pinInputs[i - 1] &&
                  +this.pinInputs[i] === +this.pinInputs[i - 2]);
            });

            // PIN number cannot be 4 sequential digits ascending or descending,
            // i.e. cannot be "1,2,3,4" or "4,3,2,1" or "6,7,8,9".
            // "6,7,8,1" would be ok.
            // PIN must have at least 2 unique digits.
            // I.e. cannot be "2,2,2,2" or "2,2,2,3".
            // "2,2,3,3" is OK though.
            if (sequentialAsc || sequentialDesc) {
              this.sequenceInvalid = true;
              this.sequenceMessage = 'PIN cannot be sequential';
              setTimeout(() => this.clearInputs(), 500);
            } else if (duplicatedChainAsc || duplicatedChainDesc) {
              this.sequenceInvalid = true;
              this.sequenceMessage = 'PIN must have 2 unique numbers';
              setTimeout(() => this.clearInputs(), 500);
            } else {
              this.pinValueComplete.emit(this.pinInputs.join(''));
            }
          } else {
            if (this.validateIncorrectPIN) {
              if (this.countRemaining > 0) {
                this.pinValueComplete.emit(this.pinInputs.join(''));
              }
            } else {
              this.pinValueComplete.emit(this.pinInputs.join(''));
            }
          }
        } else {
          this.pinValueComplete.emit('');
        }
      } else if (ev.code === 'Backspace') {
        if (this.pinInputs.every(el => !!el)) {
          this.pinValueComplete.emit('');
        }

        const backspaceIndex = index - 1 >= 0 ? index - 1 : this.pinInputs.length - 1;
        this.pinInputs[index] = '';

        setTimeout(() => {
          this.pinInputList.get(index).nativeElement.value = '';
          this.pinInputList.get(backspaceIndex).nativeElement.focus();
        });
      } else if (ev.code === 'ArrowRight') {
        setTimeout(() => {
          this.pinInputList.get(nextIndex).nativeElement.focus();
        });
      } else if (ev.code === 'ArrowLeft') {
        setTimeout(() => {
          this.pinInputList.get(prevIndex).nativeElement.focus();
        });
      }
    }

    ev.preventDefault();
    ev.stopPropagation();
  }

  clearInputs(): void {
    for (let i = 0; i < this.pinInputs.length; i++) {
      this.pinInputs[i] = '';
    }

    setTimeout(() => this.pinInputList.get(0).nativeElement.focus());
  }
}
